import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
} from '@mui/material';
import { teacherModuleAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSupervisorTeacherContext } from '../../contexts/SupervisorTeacherContext';
import HealthStatusChip from '../Reports/HealthStatusChip';
import AttendanceFilterBar from './AttendanceFilterBar';
import { buildBatchSessionPath } from '../../utils/teacherSessionNavigation';
import {
  buildAttendanceQueryParams,
  defaultAttendanceFilters,
  formatPeriodLabel,
} from '../../utils/attendanceFilters';
import { formatAttendanceSessionDate } from '../../utils/dateTimeUtils';

function attendanceStatusLabel(status, sessionKind) {
  if (sessionKind === 'holiday') return 'Holiday';
  if (!status || status === 'unmarked') return 'Unmarked';
  return String(status).charAt(0).toUpperCase() + String(status).slice(1);
}

function StatChip({ label, value }) {
  return (
    <Chip label={`${label}: ${value ?? '—'}`} color="primary" variant="outlined" sx={{ fontWeight: 500 }} />
  );
}

/**
 * @param {'teacher'|'ops'|'reports'} mode
 */
export default function StudentAttendanceView({
  mode = 'teacher',
  title = 'Student attendance',
  subtitle,
  embedded = false,
}) {
  const navigate = useNavigate();
  const { isTeacherAdmin, isAdmin, isCountryManager, isTeacherSupervisor, isFinanceSupervisor } = useAuth();
  const picker = useSupervisorTeacherContext();
  const [filters, setFilters] = useState(defaultAttendanceFilters);
  const [page, setPage] = useState(1);
  const [detailStudent, setDetailStudent] = useState(null);

  const setFiltersAndResetPage = (next) => {
    setPage(1);
    setFilters(next);
  };

  useEffect(() => {
    if (mode === 'reports' && isTeacherSupervisor()) {
      setFiltersAndResetPage((f) => ({ ...f, teamView: true }));
    }
  }, [mode, isTeacherSupervisor]);

  const useTopBarTeacherPicker =
    mode === 'teacher' && isTeacherSupervisor() && picker.isSupervisorPicker && !filters.teamView;
  const useLocalTeacherFilter =
    mode === 'ops' ||
    isTeacherAdmin() ||
    isAdmin() ||
    isFinanceSupervisor() ||
    isCountryManager() ||
    (isTeacherSupervisor() && !filters.teamView && mode === 'reports');

  const effectiveTeacherId = useMemo(() => {
    if (filters.teamView) return '';
    if (useLocalTeacherFilter && filters.teacherId !== undefined) return filters.teacherId;
    if (useTopBarTeacherPicker && picker.selectedTeacherId) return picker.selectedTeacherId;
    return '';
  }, [
    filters.teamView,
    filters.teacherId,
    useLocalTeacherFilter,
    useTopBarTeacherPicker,
    picker.selectedTeacherId,
  ]);

  useEffect(() => {
    setPage(1);
  }, [effectiveTeacherId]);

  const queryParams = useMemo(() => {
    const base = buildAttendanceQueryParams({
      ...filters,
      teacherId: effectiveTeacherId,
      page,
      pageSize: 25,
    });
    return base;
  }, [filters, effectiveTeacherId, page]);

  const adminTeachersQuery = useQuery(
    ['viewableTeachersAttendanceAdmin'],
    async () => {
      const res = await teacherModuleAPI.getViewableTeachers();
      return res.data?.data?.teachers || [];
    },
    { enabled: (isAdmin() || isFinanceSupervisor()) && !isTeacherAdmin(), staleTime: 5 * 60_000 }
  );

  const listQuery = useQuery(
    ['studentAttendance', mode, queryParams],
    async () => {
      const res = await teacherModuleAPI.getStudentAttendance(queryParams);
      return res.data?.data || {};
    },
    {
      enabled:
        mode === 'reports' ||
        mode === 'ops' ||
        filters.teamView ||
        !picker.isSupervisorPicker ||
        picker.ready,
      keepPreviousData: true,
    }
  );

  const batchesTeacherId = filters.teamView || !effectiveTeacherId ? undefined : effectiveTeacherId;
  const batchesQuery = useQuery(
    ['teacherMyBatchesForAttendance', batchesTeacherId, mode],
    async () => {
      const params = batchesTeacherId ? { teacherId: batchesTeacherId } : undefined;
      const res = await teacherModuleAPI.getMyBatches(params);
      return res.data?.data?.batches || [];
    },
    { enabled: !!batchesTeacherId && mode !== 'reports' && (mode === 'ops' || !picker.isSupervisorPicker || picker.ready) }
  );

  const sessionsQuery = useQuery(
    ['studentAttendanceSessions', detailStudent?.studentId, queryParams],
    async () => {
      const params = { ...queryParams };
      // Detail shows all batches for the student in the period (not a single batch).
      delete params.batchId;
      delete params.page;
      delete params.pageSize;
      const res = await teacherModuleAPI.getStudentAttendanceSessions(detailStudent.studentId, params);
      return res.data?.data?.sessions || [];
    },
    { enabled: !!detailStudent?.studentId }
  );

  const data = listQuery.data || {};
  const students = useMemo(() => listQuery.data?.students ?? [], [listQuery.data?.students]);
  const rosterStudents = useMemo(
    () => listQuery.data?.rosterStudents ?? [],
    [listQuery.data?.rosterStudents]
  );
  const paging = data.paging || { page: 1, pageSize: 25, total: 0, totalPages: 1 };
  const summary = data.summary || {};
  const showTeacherColumn = !!(data.teamView || data.allTeachers || data.includeTeacher);

  const batchOptions = useMemo(() => {
    const map = new Map();
    (batchesQuery.data || []).forEach((b) => map.set(b.id, b.name));
    students.forEach((s) => {
      // Prefer current batch id; skip combined "A → B" labels in the batch filter.
      if (s.batchId && s.batchName && !String(s.batchName).includes(' → ')) {
        map.set(s.batchId, s.batchName);
      }
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [batchesQuery.data, students]);

  const studentOptions = useMemo(() => {
    const seen = new Set();
    return (rosterStudents || [])
      .filter((s) => {
        const key = String(s.studentId);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((s) => ({
        studentId: s.studentId,
        studentName: s.studentName,
        displayId: s.displayId,
        batchName: s.batchName,
      }));
  }, [rosterStudents]);

  const teachersForFilter = useMemo(() => {
    const mapTeacher = (t) => ({
      id: String(t.id),
      label:
        `${t.first_name || ''} ${t.last_name || ''}`.trim() ||
        t.label ||
        t.email ||
        'Teacher',
    });
    if (mode === 'ops' || isCountryManager()) {
      return (picker.teachers || []).map((t) => ({ id: t.id, label: t.label }));
    }
    if (isTeacherAdmin() || isTeacherSupervisor()) {
      return (picker.teachers || []).map((t) => ({ id: t.id, label: t.label }));
    }
    if (isAdmin() || isFinanceSupervisor()) {
      return (adminTeachersQuery.data || []).map(mapTeacher);
    }
    return [];
  }, [
    mode,
    isCountryManager,
    isTeacherAdmin,
    isTeacherSupervisor,
    isAdmin,
    isFinanceSupervisor,
    picker.teachers,
    adminTeachersQuery.data,
  ]);

  const periodLabel = formatPeriodLabel(filters);
  const defaultSubtitle = periodLabel
    ? `${periodLabel} — class dates in each student's timezone`
    : '';

  const openStudent = (row) => {
    setDetailStudent({
      studentId: row.studentId,
      studentName: row.studentName,
      batchName: row.batchName,
    });
  };

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
      {!embedded ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle || defaultSubtitle}
            </Typography>
          </Box>
        </Stack>
      ) : null}

      {useTopBarTeacherPicker ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Viewing attendance for{' '}
          <strong>{data.viewingTeacherName || picker.selectedTeacherName || 'selected teacher'}</strong>.
          Use the teacher picker in the top bar to switch teachers, or enable &quot;All my team&quot; below.
        </Alert>
      ) : null}

      {data.viewingTeacherName && (data.teamView || data.allTeachers) ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Viewing: <strong>{data.viewingTeacherName}</strong>
        </Alert>
      ) : null}

      <AttendanceFilterBar
        filters={filters}
        onChange={setFiltersAndResetPage}
        batches={batchOptions}
        students={studentOptions}
        teachers={teachersForFilter}
        showTeacherFilter={useLocalTeacherFilter && !filters.teamView}
        showTeamToggle={isTeacherSupervisor() && mode !== 'ops'}
        allowAllTeachers={isTeacherAdmin() || isAdmin() || isFinanceSupervisor() || isCountryManager() || mode === 'ops'}
        compact={embedded}
      />

      {summary.totalStudents > 0 ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <StatChip label="Students" value={summary.totalStudents} />
          <StatChip label="Need attention (this page)" value={summary.needsAttention} />
        </Stack>
      ) : null}

      {data.endCap && data.to && data.endCap < data.to ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Counts include classes through {data.endCap} (period in progress).
        </Typography>
      ) : null}

      {listQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : listQuery.isError ? (
        <Alert severity="error">Failed to load attendance data.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Batch</TableCell>
                {showTeacherColumn ? (
                  <TableCell sx={{ fontWeight: 700 }}>Teacher</TableCell>
                ) : null}
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Sessions
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Present
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Absent
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Makeup
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Unmarked
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Cancelled
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Holiday
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Rate
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((row) => (
                <TableRow
                  key={row.studentId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openStudent(row)}
                >
                  <TableCell>
                    <Typography fontWeight={600}>{row.studentName}</Typography>
                    {row.displayId ? (
                      <Typography variant="caption" color="text.secondary">
                        {row.displayId}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{row.batchName}</TableCell>
                  {showTeacherColumn ? <TableCell>{row.teacherName || '—'}</TableCell> : null}
                  <TableCell align="right">{row.sessionsEligible}</TableCell>
                  <TableCell align="right">{row.present}</TableCell>
                  <TableCell align="right">{row.absent}</TableCell>
                  <TableCell align="right">{row.makeup}</TableCell>
                  <TableCell align="right">{row.unmarked}</TableCell>
                  <TableCell align="right">{row.cancelled ?? 0}</TableCell>
                  <TableCell align="right">{row.holiday ?? 0}</TableCell>
                  <TableCell align="right">
                    {row.attendanceRate != null ? `${row.attendanceRate}%` : '—'}
                  </TableCell>
                  <TableCell>
                    <HealthStatusChip status={row.status} />
                  </TableCell>
                </TableRow>
              ))}
              {!students.length ? (
                <TableRow>
                  <TableCell colSpan={showTeacherColumn ? 12 : 11} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No students with classes in this period. Try different filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {paging.total > 0 ? (
        <TablePagination
          component="div"
          count={paging.total}
          page={Math.max(0, (paging.page || page) - 1)}
          onPageChange={(_e, next) => setPage(next + 1)}
          rowsPerPage={paging.pageSize || 25}
          rowsPerPageOptions={[25]}
          onRowsPerPageChange={() => {}}
        />
      ) : null}

      <Dialog open={!!detailStudent} onClose={() => setDetailStudent(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {detailStudent?.studentName}
          {detailStudent?.batchName ? ` · ${detailStudent.batchName}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {sessionsQuery.isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Attendance</TableCell>
                  <TableCell align="right">Open</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(sessionsQuery.data || []).map((s) => (
                  <TableRow key={s.sessionId}>
                    <TableCell>{formatAttendanceSessionDate(s)}</TableCell>
                    <TableCell>{s.batchName}</TableCell>
                    <TableCell>{s.sessionKind}</TableCell>
                    <TableCell>{attendanceStatusLabel(s.attendanceStatus, s.sessionKind)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => {
                          const path = buildBatchSessionPath(s.batchId, s.sessionId, {
                            action: s.attendanceStatus === 'unmarked' ? 'attendance' : undefined,
                          });
                          if (path) navigate(path);
                        }}
                      >
                        Class
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!sessionsQuery.data?.length ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No sessions in this period for this student.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailStudent(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
